import {
  Avatar,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Autosizer, Scrollbar } from 'app/components';
import { TransportGroupRoundedIcon } from 'app/icons';
import { useAsyncAction } from 'app/services';
import { userTransportGroupDelete, userTransportGroupListDelete, userTransportGroupListPush, userTransportGroupPush } from 'app/store/user';
import React, { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const UserTransportGroupList = ({ searchValue }) => {
  const { selectedItem } = useSelector((state) => state.user);
  const transportGroups = selectedItem.transportGroups;
  const checkedLength = transportGroups?.filter((item) => item.checked).length;
  const isCheckedAll = checkedLength === transportGroups?.length;
  const { asyncAction: deleteSubmit, isLoading: isDeleteSubmit } = useAsyncAction(userTransportGroupDelete, [selectedItem]);

  const { asyncAction: deleteGroup, isLoading: isDeleteGroup } = useAsyncAction(userTransportGroupListDelete, [selectedItem]);

  const { asyncAction: addGroup, isLoading: isAddGroup } = useAsyncAction(userTransportGroupListPush, [selectedItem]);

  const { asyncAction: pushSubmit, isLoading: isPushSubmit } = useAsyncAction(userTransportGroupPush, [selectedItem]);

  const items = useMemo(() => {
    if (searchValue?.length) {
      return transportGroups.filter((item) => item.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
    }

    return transportGroups;
  }, [selectedItem, searchValue]);
  const handleItemClick = (value) => {
    value.checked ? deleteSubmit(selectedItem.id, value.id) : pushSubmit(selectedItem.id, value.id);
  };

  const handleCheckAll = () => {
    if (isAddGroup || isDeleteGroup) return;
    if (isCheckedAll) {
      deleteGroup(
        selectedItem.id,
        transportGroups.map((item) => item.id)
      );
    } else {
      addGroup(
        selectedItem.id,
        transportGroups.filter((item) => !item.checked).map((item) => item.id)
      );
    }
  };

  return (
    <Box
      component='div'
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        '.simplebar-offset > div': {
          paddingBottom: '36px',
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.MuiListItem-root': {
          width: '100%',
          userSelect: 'none',
          padding: theme.spacing(0),
          borderRadius: '0',
          '.MuiListItemIcon-root': {
            justifyContent: 'end',
            '.MuiCheckbox-root': {
              padding: theme.spacing(0),
              marginRight: theme.spacing(0),
              svg: {
                fontSize: '18px',
              },
            },
          },
          '.MuiListItemAvatar-root': {
            minWidth: '36px',
          },
          '.MuiAvatar-circular': {
            width: '24px',
            height: '24px',
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            svg: {
              fontSize: '16px',
              path: {
                fill: theme.palette.primary.main,
              },
            },
          },
          '.MuiListItemButton-root': {
            padding: theme.spacing(1, 2),
            borderRadius: '0',
          },
          '.MuiListItemText-root': {
            margin: theme.spacing(0),
          },
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <Box
              sx={{
                textAlign: 'end',
                paddingRight: '14px',
                cursor: 'pointer',
                font: 'inherit',
              }}
              onClick={handleCheckAll}>
              <Typography component='span'>Выделить всё</Typography>
              <Checkbox size='small' edge='end' disableRipple checked={isCheckedAll} disabled={isAddGroup || isDeleteGroup} />
            </Box>
            <Scrollbar>
              {({ scrollableNodeRef, contentNodeRef }) => (
                <FixedSizeList
                  height={height}
                  itemSize={36}
                  overscanCount={10}
                  itemCount={items.length}
                  innerRef={contentNodeRef}
                  outerRef={scrollableNodeRef}
                  innerElementType={UserTransportGroupListInnerElementType}
                  itemData={{
                    items,
                    onClick: handleItemClick,
                    isPushSubmit,
                    isDeleteSubmit,
                    isAddGroup,
                    isDeleteGroup,
                  }}>
                  {UserTransportGroupListItemRender}
                </FixedSizeList>
              )}
            </Scrollbar>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const UserTransportGroupListInnerElementType = forwardRef((props, ref) => <List ref={ref} disablePadding {...props} />);

const UserTransportGroupListItemRender = ({ data, index, style }) => {
  const { items, onClick, isPushSubmit, isDeleteSubmit, isAddGroup, isDeleteGroup } = data;

  return (
    <ListItem key={items[index]?.id} style={style} onClick={() => onClick(items[index])}>
      <ListItemButton>
        <ListItemAvatar>
          <Avatar>
            <TransportGroupRoundedIcon fontSize='small' />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={items[index]?.name} />
        <ListItemIcon>
          <Checkbox
            size='small'
            edge='end'
            disableRipple
            disabled={isPushSubmit || isDeleteSubmit || isAddGroup || isDeleteGroup}
            checked={items[index]?.checked}
          />
        </ListItemIcon>
      </ListItemButton>
    </ListItem>
  );
};

export default UserTransportGroupList;
